import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Upload, FileText, Download, Trash2, Calendar, Plus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DocumentUploader = ({ entityType, entityId, entityName }) => {
  const { getAuthHeaders } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadDate, setUploadDate] = useState(new Date().toISOString().split('T')[0]);
  const [uploadNotes, setUploadNotes] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchDocuments();
    fetchCategories();
  }, [entityId]);

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${API}/documents?entity_type=${entityType}&entity_id=${entityId}`, getAuthHeaders());
      setDocuments(res.data || []);
    } catch { setDocuments([]); }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/documents/categories`, getAuthHeaders());
      setCategories(res.data[entityType] || []);
    } catch { setCategories([]); }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadCategory) {
      toast.error('Please select a file and category');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const params = new URLSearchParams({
        entity_type: entityType,
        entity_id: entityId,
        category: uploadCategory,
        document_date: uploadDate,
        notes: uploadNotes,
      });
      await axios.post(`${API}/documents/upload?${params.toString()}`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully');
      setShowUpload(false);
      setSelectedFile(null);
      setUploadCategory('');
      setUploadNotes('');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/documents/${doc.id}/download`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/documents/${docId}`, getAuthHeaders());
      toast.success('Document deleted');
      fetchDocuments();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const getCategoryLabel = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? cat.label : catId;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm text-muted-foreground flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Documents ({documents.length})
        </h4>
        <Button size="sm" variant="outline" onClick={() => setShowUpload(!showUpload)} data-testid="upload-doc-btn">
          <Plus className="w-3 h-3 mr-1" />
          Upload
        </Button>
      </div>

      {showUpload && (
        <Card className="border-dashed border-2">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Category</Label>
                <Select value={uploadCategory} onValueChange={setUploadCategory}>
                  <SelectTrigger className="h-8 text-xs" data-testid="doc-category-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Document Date</Label>
                <Input type="date" value={uploadDate} onChange={(e) => setUploadDate(e.target.value)} className="h-8 text-xs" data-testid="doc-date-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Notes (optional)</Label>
              <Input value={uploadNotes} onChange={(e) => setUploadNotes(e.target.value)} placeholder="e.g., Expires 2028" className="h-8 text-xs" data-testid="doc-notes-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">File (PDF, images, Word — max 5MB)</Label>
              <Input type="file" accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx" onChange={(e) => setSelectedFile(e.target.files[0])} className="h-8 text-xs" data-testid="doc-file-input" />
            </div>
            {uploadCategory && categories.find(c => c.id === uploadCategory)?.compliance_field && (
              <p className="text-xs text-green-600">
                This will auto-update the compliance date to {uploadDate}
              </p>
            )}
            <div className="flex gap-2">
              <Button size="sm" onClick={handleUpload} disabled={uploading || !selectedFile || !uploadCategory} data-testid="doc-upload-submit">
                {uploading ? 'Uploading...' : 'Upload Document'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowUpload(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {documents.length > 0 ? (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div key={doc.id} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg border text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium truncate">{doc.filename}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant="outline" className="text-[10px] px-1">{getCategoryLabel(doc.category)}</Badge>
                    <span>{new Date(doc.document_date + 'T12:00:00').toLocaleDateString()}</span>
                    <span>{formatFileSize(doc.file_size)}</span>
                    {doc.uploaded_by_name && <span>by {doc.uploaded_by_name}</span>}
                  </div>
                  {doc.notes && <p className="text-xs text-muted-foreground italic mt-0.5">{doc.notes}</p>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handleDownload(doc)} data-testid={`download-doc-${doc.id}`}>
                  <Download className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500" onClick={() => handleDelete(doc.id)} data-testid={`delete-doc-${doc.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        !showUpload && <p className="text-xs text-muted-foreground text-center py-2">No documents uploaded yet</p>
      )}
    </div>
  );
};

export default DocumentUploader;
